import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';

export const WINDOW_REF = new InjectionToken<Window>('WindowRef', {
  factory: () => window,
});

interface QueuedEvent {
  eventName: string;
  data: Record<string, any>;
  timestamp: number;
}

const STORAGE_KEY = 'nr_offline_queue';
const MAX_QUEUE_SIZE = 100;
const MAX_AGE_MS = 10 * 60 * 1000; // 10min

/**
 * Servicio de cola offline para eventos de New Relic.
 *
 * Persiste eventos en localStorage cuando no hay conexión a internet y los
 * reenvía automáticamente a New Relic cuando la red se restaura.
 *
 * Detección de estado offline/online:
 * - Utiliza la propiedad nativa del browser `navigator.onLine` para saber si hay red.
 * - Escucha el evento nativo `window.online` que el browser dispara automáticamente
 *   cuando detecta que la conexión de red se restauró.
 * - No usa polling ni intervalos; es el propio browser quien notifica el cambio.
 *
 * Límites:
 * - Máximo 100 eventos en cola (descarta los más antiguos si se excede).
 * - Los eventos expiran después de 10 minutos (MAX_AGE_MS).
 * - Si localStorage no está disponible o está lleno, las operaciones fallan silenciosamente.
 */
@Injectable({
  providedIn: 'root',
})
export class OfflineEventQueueService implements OnDestroy {
  private onlineHandler = () => this.flush();

  constructor(@Inject(WINDOW_REF) private win: Window) {
    this.win.addEventListener('online', this.onlineHandler);
  }

  ngOnDestroy(): void {
    this.win.removeEventListener('online', this.onlineHandler);
  }

  get isOnline(): boolean {
    return this.win.navigator.onLine;
  }

  /**
   * Agrega un evento a la cola offline en localStorage.
   * Si la cola supera MAX_QUEUE_SIZE (100), descarta los eventos más antiguos.
   * @param eventName - Nombre del evento de New Relic.
   * @param data - Datos asociados al evento.
   */
  enqueue(eventName: string, data: Record<string, any>): void {
    const queue = this.getQueue();
    queue.push({ eventName, data, timestamp: Date.now() });
    if (queue.length > MAX_QUEUE_SIZE) {
      queue.splice(0, queue.length - MAX_QUEUE_SIZE);
    }
    this.saveQueue(queue);
  }

  /**
   * Envía todos los eventos pendientes a New Relic y limpia la cola.
   * Solo se ejecuta si hay conexión (navigator.onLine === true).
   * Descarta eventos con más de 10 minutos de antigüedad (MAX_AGE_MS).
   * Si un evento individual falla al enviarse, continúa con los demás.
   * Se invoca automáticamente cuando el browser dispara el evento 'online'.
   */
  flush(): void {
    if (!this.isOnline) return;

    const newRelic = (this.win as any).newrelic;
    if (!newRelic) {
      return;
    }

    const queue = this.getQueue();
    if (!queue.length) return;

    const now = Date.now();
    const validEvents = queue.filter((e) => now - e.timestamp < MAX_AGE_MS);

    for (const event of validEvents) {
      try {
        newRelic.addPageAction(event.eventName, event.data);
      } catch {
      }
    }

    this.clearQueue();
  }

  private getQueue(): QueuedEvent[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private saveQueue(queue: QueuedEvent[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch {
    }
  }

  private clearQueue(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
    }
  }
}
